import React, { useEffect, useState } from "react";
import { shareApi, formatApiError, activityApi } from "./api";
import { Link2, Copy, Check, Trash2, Eye, Clock } from "lucide-react";

const EXPIRY_OPTS: { label: string; days: number | null }[] = [
  { label: "Tanpa kedaluwarsa", days: null },
  { label: "7 hari", days: 7 },
  { label: "30 hari", days: 30 },
  { label: "90 hari", days: 90 },
];

function fmtDate(iso?: string | null): string {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("id-ID", { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

export default function ShareDialog({ gid, groupName, onClose }: { gid: string; groupName: string; onClose: () => void }) {
  const [token, setToken] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [expiryDays, setExpiryDays] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState("");

  const url = token ? `${window.location.origin}/share/${token}` : "";
  const isExpired = !!expiresAt && new Date(expiresAt).getTime() < Date.now();

  useEffect(() => {
    shareApi.get(gid)
      .then((r) => { setToken(r.share?.token ?? null); setExpiresAt(r.share?.expires_at ?? null); })
      .catch((e) => setErr(formatApiError(e?.detail)))
      .finally(() => setLoading(false));
  }, [gid]);

  const create = async () => {
    setBusy(true); setErr("");
    try {
      const r = await shareApi.create(gid, expiryDays);
      setToken(r.token); setExpiresAt(r.expires_at ?? null);
      activityApi.log({ action: "share_buat", target_type: "grup", target_name: groupName, group_id: gid, group_name: groupName });
    }
    catch (e: any) { setErr(formatApiError(e?.detail)); }
    finally { setBusy(false); }
  };

  const applyExpiry = async () => {
    setBusy(true); setErr("");
    try {
      const r = await shareApi.create(gid, expiryDays);
      setToken(r.token); setExpiresAt(r.expires_at ?? null);
    }
    catch (e: any) { setErr(formatApiError(e?.detail)); }
    finally { setBusy(false); }
  };

  const revoke = async () => {
    if (!window.confirm("Nonaktifkan tautan ini? Siapa pun yang memegang link tidak bisa lagi membukanya.")) return;
    setBusy(true); setErr("");
    try { await shareApi.revoke(gid); setToken(null); setExpiresAt(null); activityApi.log({ action: "share_cabut", target_type: "grup", target_name: groupName, group_id: gid, group_name: groupName }); }
    catch (e: any) { setErr(formatApiError(e?.detail)); }
    finally { setBusy(false); }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1600); } catch { /* ignore */ }
  };

  const expirySelect = (
    <select className="filter-select" value={expiryDays === null ? "" : String(expiryDays)}
      onChange={(e) => setExpiryDays(e.target.value === "" ? null : Number(e.target.value))} data-testid="share-expiry-select">
      {EXPIRY_OPTS.map((o) => <option key={o.label} value={o.days === null ? "" : String(o.days)}>{o.label}</option>)}
    </select>
  );

  return (
    <div className="modal-overlay" data-testid="share-dialog" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ width: 540, maxWidth: "94vw" }}>
        <div className="modal-title serif"><Link2 size={18} style={{ verticalAlign: "-3px", marginRight: 8 }} />Bagikan bagan — {groupName}</div>
        <div className="modal-sub">Buat tautan tampilan-saja. Penerima dapat melihat bagan, timeline tahap, kepemilikan efektif/UBO, dan status dokumen tanpa perlu login. Mereka tidak dapat mengubah apa pun.</div>
        {err && <div className="auth-err" data-testid="share-error">{err}</div>}

        {loading ? (
          <div style={{ padding: "18px 0", color: "var(--muted)" }}>Memuat…</div>
        ) : token ? (
          <>
            <div className="share-link-row">
              <input className="share-link-input" readOnly value={url} data-testid="share-url" onFocus={(e) => e.target.select()} />
              <button className="btn on" onClick={copy} data-testid="share-copy" title="Salin tautan">
                {copied ? <Check size={15} /> : <Copy size={15} />} {copied ? "Tersalin" : "Salin"}
              </button>
            </div>

            <div className="share-expiry-box" data-testid="share-expiry-status" style={{ display: "flex", alignItems: "center", gap: 8, margin: "12px 0", flexWrap: "wrap" }}>
              <Clock size={15} style={{ color: isExpired ? "var(--danger2, #b0244f)" : "var(--muted)" }} />
              <span style={{ color: isExpired ? "var(--danger2, #b0244f)" : "var(--secondary)", fontWeight: 600 }}>
                {isExpired ? `Kedaluwarsa pada ${fmtDate(expiresAt)}` : expiresAt ? `Berlaku sampai ${fmtDate(expiresAt)}` : "Tanpa kedaluwarsa"}
              </span>
            </div>
            <div className="share-expiry-edit" style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <span className="cell-sub">Masa berlaku:</span>
              {expirySelect}
              <button className="btn" onClick={applyExpiry} disabled={busy} data-testid="share-expiry-apply">Terapkan</button>
            </div>

            <div className="share-actions">
              <a className="share-preview" href={url} target="_blank" rel="noreferrer" data-testid="share-preview"><Eye size={14} /> Pratinjau tautan</a>
              <div style={{ flex: 1 }} />
              <button className="btn danger" onClick={revoke} disabled={busy} data-testid="share-revoke"><Trash2 size={14} /> Nonaktifkan tautan</button>
            </div>
          </>
        ) : (
          <div className="share-empty">
            <p>Belum ada tautan untuk grup ini.</p>
            <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "center", marginBottom: 12 }}>
              <span className="cell-sub">Masa berlaku:</span>
              {expirySelect}
            </div>
            <button className="btn on" onClick={create} disabled={busy} data-testid="share-create"><Link2 size={15} /> Buat tautan bagikan</button>
          </div>
        )}

        <div className="modal-actions">
          <div style={{ flex: 1 }} />
          <button className="btn" onClick={onClose} data-testid="share-close">Tutup</button>
        </div>
      </div>
    </div>
  );
}
